import React, { useRef, useState, useEffect } from "react";
import useAudioAnswer from "../../utils/useAudioAnswer";
import AudioPlayer from "../../utils/audioPlayer";

const SpeakingSection = ({ section, flattenedQuestions, submitAnswer, onRequestNextSection, assignmentId }) => {
  const timerRef = useRef(null);
  const playerRef = useRef(null);

  const [index, setIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [isPlaying, setIsPlaying] = useState(false);

  const {
    phase,
    isRecording,
    hasRecording,
    uploadedUrl,
    uploadedDuration,
    uploadError,
    beginRecording,
    finishRecording,
    retryUpload,
    recordAgain,
    stopRecording,
  } = useAudioAnswer({ assignmentId, filePrefix: "recording" });

  const isCompleted = (q) => {
    if (!q?.previous_answer?.answer) return false;
    const data = q.previous_answer.answer.answer_data;
    if (data?.status === "completed") return true;
    if (typeof data === "string" && data.trim() !== "") return true;
    if (typeof data === "object" && Object.keys(data).length > 0) return true;
    return false;
  };

  useEffect(() => {
    let nextIndex = index;

    while (
      nextIndex < flattenedQuestions.length &&
      isCompleted(flattenedQuestions[nextIndex])
    ) {
      nextIndex++;
    }

    if (nextIndex !== index) {
      if (nextIndex >= flattenedQuestions.length) {
        onRequestNextSection && onRequestNextSection();
      } else {
        setIndex(nextIndex);
      }
    }
  }, [section, index, flattenedQuestions]);

  useEffect(() => {
    setIndex(0);
    setTimeLeft(60);
    recordAgain();
  }, [section]);

  // Fresh recorder state whenever we move to another question.
  useEffect(() => {
    setTimeLeft(60);
    setIsPlaying(false);
    recordAgain();
  }, [index]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      stopRecording();
    };
  }, [section]);

  const current = flattenedQuestions[index];

  if (!current) return <div className="language-right-section">No questions</div>;

  const startTimedRecording = () => {
    if (isRecording) return;
    setTimeLeft(60);
    beginRecording(current);

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          finishRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopTimedRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    finishRecording();
  };

  const handleRecordAgain = () => {
    setTimeLeft(60);
    setIsPlaying(false);
    recordAgain();
  };

  const handleSubmit = () => {
    if (phase !== "ready" || !uploadedUrl) return;

    const answer = {
      answer_data: {
        type: "audio_recording",
        audio_url: uploadedUrl,
        duration_seconds: uploadedDuration,
        status: "completed",
        timestamp: new Date().toISOString(),
      },
    };

    submitAnswer({
      assignment_question_id: current.assignment_question_id,
      question_id: current.id,
      answer,
    });

    if (index < flattenedQuestions.length - 1) {
      setIndex(index + 1);
      handleRecordAgain();
    } else {
      onRequestNextSection && onRequestNextSection();
    }
  };

  return (
    <>
      <div className="language-left-section speaking" style={{ gap: '12px' }}>
        <img src="/images/speaker-gray.svg" alt="" width={56} height={56} />
        {current.content?.instructions && (
          <p className="instructions">{current.content.instructions}</p>
        )}
        <p>{current.text}</p>
        {phase === "ready" && uploadedUrl && (
          <AudioPlayer audioUrl={uploadedUrl} ref={playerRef} onPlayStateChange={setIsPlaying} />
        )}
      </div>
      <div className="language-right-section speaking-right">
        <p style={{ marginBottom: '0' }}>Record your response by clicking below toggle</p>

        <div className="recorder">
          <div className="text-center">
            {!isRecording && !hasRecording && (
              <div className="wight-mic-bg" onClick={startTimedRecording} style={{ cursor: "pointer" }}>
                <img title="Start Recording" src="/images/mic-icon.svg" alt="" width={56} height={56} />
              </div>
            )}

            {isRecording && (
              <div className="wight-mic-bg" onClick={stopTimedRecording} style={{ cursor: "pointer" }}>
                <img title="Stop Recording" src="/images/reddot-img.svg" alt="" width={71} height={71} />
              </div>
            )}

            {!isRecording && phase === "uploading" && (
              <div className="wight-mic-bg" style={{ opacity: 0.6 }}>
                <img title="Saving" src="/images/mic-icon.svg" alt="" width={56} height={56} />
              </div>
            )}

            {!isRecording && phase === "error" && (
              <div className="wight-mic-bg" onClick={retryUpload} style={{ cursor: "pointer" }}>
                <img title="Retry upload" src="/images/refresh-outline.svg" alt="" width={40} height={40} />
              </div>
            )}

            {!isRecording && phase === "ready" && (
              <div className="wight-mic-bg" style={{ cursor: "pointer" }}
                onClick={() => {
                  if (!playerRef.current) return;
                  if (isPlaying) playerRef.current.pause();
                  else playerRef.current.play();
                }}
              >
                <img title="Play-Pause" src={isPlaying ? "/images/pause-icon.svg" : "/images/play-icon.svg"} alt="" width={56} height={56} />
              </div>
            )}

            {!isRecording && phase === "uploading" && <span>Saving your recording…</span>}
            {!isRecording && phase === "ready" && <span>Play to listen</span>}
            {!isRecording && phase === "error" && (
              <span className="error">{uploadError || "Upload failed. Please retry."}</span>
            )}
          </div>

          {isRecording && (
            <div className="recording-state">
              <div className="big-dot" />
              <p>Recording... {timeLeft}s left</p>
            </div>
          )}

          {!isRecording && hasRecording && (
            <div className="recorder-actions">
              {phase === "error" ? (
                <button className="primary" onClick={retryUpload}>Retry upload</button>
              ) : (
                <button
                  className="primary"
                  onClick={handleSubmit}
                  disabled={phase !== "ready"}
                >
                  {phase === "uploading" ? "Uploading…" : "Submit"}
                </button>
              )}
              <p onClick={handleRecordAgain}>
                <img src="/images/refresh-outline.svg" alt="" width={16} height={16} />
                Replay question and record again
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SpeakingSection;
